import { useRef, useState, useEffect } from "react";
import {
  createStudent,
  updateStudent,
  getStudentById,
} from "../../api/studentsApi";
import {
  validateStudentRegistration,
  getMaxDateOfBirthForStudent,
  toDateInputValue,
} from "../../utils/studentFormValidation";
import { getCsrfToken } from "../../../../public/api/authApi";
import {
  GlobalButton,
  GlobalContactNumberField,
  GlobalDateField,
  GlobalEmailField,
  GlobalSelectField,
  GlobalTextAreaField,
  GlobalTextField,
  GlobalPasswordField,
} from "../../../../global/forms/GlobalFormFields";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import GlobalSuccessFlashModal from "../../../../global/GlobalSuccessFlashModal";
import { masterSelectOptions } from "../../../memberships/utils/masterLookupUtils";

const initialForm = {
  full_name: "",
  date_of_birth: "",
  gender: "male",
  address: "",
  contact_number: "",
  email: "",
  password: "",
  password_confirm: "",
  guardian_name: "",
  emergency_contact: "",
  sport_interested_id: "",
};

const ALERT_ID = "student-reg-form-alert";

const maxDate = getMaxDateOfBirthForStudent();

export default function StudentRegistrationForm({
  sports,
  editingStudentId = null,
  onCancelEdit,
  onRegistered,
}) {
  const [formData, setFormData] = useState(initialForm);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingStudent, setLoadingStudent] = useState(false);
  const [errorDetails, setErrorDetails] = useState({});
  const alertRef = useRef(null);
  const isEditing = editingStudentId != null;

  useEffect(() => {
    if (!editingStudentId) {
      setFormData(initialForm);
      setError("");
      setErrorDetails({});
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoadingStudent(true);
      setError("");
      setErrorDetails({});
      setSuccess("");
      try {
        const res = await getStudentById(editingStudentId);
        const s = res.student;
        if (cancelled || !s) {
          if (!s && !cancelled) setError("Student not found.");
          return;
        }
        setFormData({
          full_name: s.full_name ?? "",
          date_of_birth: toDateInputValue(s.date_of_birth),
          gender: (s.gender || "male").toLowerCase(),
          address: s.address ?? "",
          contact_number: s.contact_number ? String(s.contact_number) : "",
          email: s.email ?? "",
          password: "",
          password_confirm: "",
          guardian_name: s.guardian_name ?? "",
          emergency_contact: s.emergency_contact
            ? String(s.emergency_contact)
            : "",
          sport_interested_id:
            s.sport_interested_id != null && s.sport_interested_id !== ""
              ? String(s.sport_interested_id)
              : "",
        });
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load student.");
      } finally {
        if (!cancelled) setLoadingStudent(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [editingStudentId]);

  const setFormField = (name, value) => {
    setSuccess("");
    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleCancelEdit = () => {
    setFormData(initialForm);
    setError("");
    setErrorDetails({});
    setSuccess("");
    onCancelEdit?.();
  };

  const handleCancelRegister = () => {
    setFormData(initialForm);
    setError("");
    setErrorDetails({});
    setSuccess("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    setErrorDetails({});
    setIsSubmitting(true);

    const validation = validateStudentRegistration(formData, {
      passwordMode: isEditing ? "optional" : "required",
    });
    if (!validation.ok) {
      setError("Please correct the fields below.");
      setErrorDetails(validation.errors);
      setIsSubmitting(false);
      setTimeout(() => alertRef.current?.focus(), 0);
      return;
    }

    const { payload } = validation;

    let csrfToken = localStorage.getItem("csrf-token");
    if (!csrfToken) {
      csrfToken = await getCsrfToken();
      localStorage.setItem("csrf-token", csrfToken);
    }

    try {
      if (isEditing) {
        const {
          photo: _p,
          password: newPassword,
          password_confirm: _pc,
          ...restBody
        } = payload;
        void _p;
        void _pc;
        const updateBody = { ...restBody };
        if (newPassword) {
          updateBody.password = newPassword;
        }
        await updateStudent(editingStudentId, updateBody, csrfToken);
        setError("");
        setErrorDetails({});
        setSuccess("Student updated successfully.");
        onRegistered?.();
      } else {
        const { error: apiError, data } = await createStudent(
          csrfToken,
          payload,
        );

        if (apiError) {
          setError(apiError.message || "Failed to register student.");
          setErrorDetails(apiError.details || {});
          setTimeout(() => alertRef.current?.focus(), 0);
        } else if (data) {
          setError("");
          setErrorDetails({});
          const registered = data.data;
          const baseMsg =
            typeof data.message === "string" && data.message.trim()
              ? data.message.trim()
              : "Student registered successfully.";
          setSuccess(
            registered?.code
              ? `${baseMsg} Student code: ${registered.code}`
              : baseMsg,
          );
          setFormData(initialForm);
          onRegistered?.();
        }
      }
    } catch (e) {
      setError(e.message || "Request failed.");
      setTimeout(() => alertRef.current?.focus(), 0);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <GlobalSuccessFlashModal
        message={success}
        onDismiss={() => setSuccess("")}
      />
      {loadingStudent ? (
        <p className="student-reg-form-loading" style={{ margin: "0 0 1rem" }}>
          Loading student…
        </p>
      ) : null}
      <GlobalFormShell
        legend={isEditing ? "Edit student" : "Register Student"}
        showAlert={!!error}
        alertText={error}
        alertId={ALERT_ID}
        formAlertRef={alertRef}
        actions={
          <>
            {isEditing ? (
              <GlobalButton
                type="button"
                variant="ghost"
                onClick={handleCancelEdit}
                disabled={isSubmitting}
              >
                Cancel edit
              </GlobalButton>
            ) : null}
            <GlobalButton
              type="submit"
              variant="primary"
              disabled={isSubmitting || loadingStudent}
            >
              {isSubmitting
                ? isEditing
                  ? "Saving…"
                  : "Submitting..."
                : isEditing
                  ? "Save changes"
                  : "Submit Registration"}
            </GlobalButton>
            {!isEditing ? (
              <GlobalButton
                type="button"
                variant="danger"
                onClick={handleCancelRegister}
                disabled={isSubmitting || loadingStudent}
              >
                Cancel
              </GlobalButton>
            ) : null}
          </>
        }
      >
        <GlobalTextField
          id="full_name"
          label="Full Name"
          value={formData.full_name}
          onChange={(v) => setFormField("full_name", v)}
          error={errorDetails.full_name}
          nameTextRules
          baseAlertId={ALERT_ID}
          required
        />
        <GlobalDateField
          id="date_of_birth"
          label="Date of Birth"
          value={formData.date_of_birth}
          onChange={(v) => setFormField("date_of_birth", v)}
          error={errorDetails.date_of_birth}
          max={maxDate}
          baseAlertId={ALERT_ID}
          required
        />
        <GlobalSelectField
          id="gender"
          label="Gender"
          value={formData.gender}
          onChange={(v) => setFormField("gender", v)}
          options={[
            { label: "Male", value: "male" },
            { label: "Female", value: "female" },
            { label: "Others", value: "others" },
          ]}
          baseAlertId={ALERT_ID}
          required
        />
        <GlobalContactNumberField
          id="contact_number"
          label="Mobile Number"
          value={formData.contact_number}
          onChange={(v) => setFormField("contact_number", v)}
          error={errorDetails.contact_number}
          baseAlertId={ALERT_ID}
          required
        />
        <GlobalEmailField
          id="email"
          label="Email ID"
          value={formData.email}
          onChange={(v) => setFormField("email", v)}
          error={errorDetails.email}
          emailFormatRules
          baseAlertId={ALERT_ID}
          required
        />
        {isEditing ? (
          <p
            className="gff-field gff-field--full student-reg-password-hint"
            style={{
              margin: "0 0 0.25rem",
              fontSize: "0.75rem",
              color: "#64748b",
            }}
          >
            Leave both password fields blank to keep the current password.
          </p>
        ) : null}
        <GlobalPasswordField
          id="password"
          label={isEditing ? "New password (optional)" : "Password"}
          value={formData.password}
          onChange={(v) => setFormField("password", v)}
          error={errorDetails.password}
          passwordPolicyRules
          baseAlertId={ALERT_ID}
          autoComplete="new-password"
          required={!isEditing}
        />
        <GlobalPasswordField
          id="password_confirm"
          label={
            isEditing ? "Confirm new password (optional)" : "Confirm password"
          }
          value={formData.password_confirm}
          onChange={(v) => setFormField("password_confirm", v)}
          error={errorDetails.password_confirm}
          passwordPolicyRules
          baseAlertId={ALERT_ID}
          autoComplete="new-password"
          required={!isEditing}
        />
        <GlobalTextField
          id="guardian_name"
          label="Parent / Guardian Name"
          value={formData.guardian_name}
          onChange={(v) => setFormField("guardian_name", v)}
          error={errorDetails.guardian_name}
          nameTextRules
          baseAlertId={ALERT_ID}
          required
        />
        <GlobalContactNumberField
          id="emergency_contact"
          label="Emergency Contact Number"
          value={formData.emergency_contact}
          onChange={(v) => setFormField("emergency_contact", v)}
          error={errorDetails.emergency_contact}
          baseAlertId={ALERT_ID}
          required
        />
        <GlobalSelectField
          id="sport_interested_id"
          label="Sport Interested"
          value={formData.sport_interested_id}
          onChange={(v) => setFormField("sport_interested_id", v)}
          options={masterSelectOptions(sports, {
            valueKey: "id",
            labelKey: "sport_name",
            selectedValue: formData.sport_interested_id,
          })}
          error={errorDetails.sport_interested_id}
          baseAlertId={ALERT_ID}
          required
        />
        <GlobalTextAreaField
          id="address"
          label="Address"
          value={formData.address}
          onChange={(v) => setFormField("address", v)}
          error={errorDetails.address}
          baseAlertId={ALERT_ID}
          required
        />
      </GlobalFormShell>
    </form>
  );
}
